// frontend/packages/business-console/src/index.js
//
// Sentry init MUST come first. @sentry/react installs window.onerror and
// unhandledrejection handlers at init() time; anything that throws before
// initSentry() is dropped.
import { initSentry, ErrorBoundary as SentryErrorBoundary } from "./sentry";
initSentry();

import React from "react";
import ReactDOM from "react-dom/client";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { MsalProvider } from "@azure/msal-react";

import App from "./pages/App";
import { ThemeProvider } from "./theme";
import { AuthProvider } from "./contexts/AuthContext";
import { initializeMsal, getMsalInstance } from "./auth/msalConfig";
import { registerServiceWorker } from "./pwa/serviceWorkerRegistration";

const isProduction = process.env.NODE_ENV === "production";

function devLog(level, ...args) {
  if (isProduction) return;
  // eslint-disable-next-line no-console
  const c = console;
  if (level === "error") c.error(...args);
  else if (level === "warn") c.warn(...args);
  else c.info(...args);
}

function FullScreenMessage({ loading = false, errorMessage = "", error = null }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        p: 4,
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <Box sx={{ maxWidth: 520, textAlign: "center" }}>
        {loading ? (
          <>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography variant="h6">Preparing sign-in…</Typography>
          </>
        ) : (
          <>
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || "An unexpected error occurred during sign-in initialization."}
            </Alert>

            {!isProduction && error?.message ? (
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                {error.message}
              </Typography>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Missing #root element");

const root = ReactDOM.createRoot(rootElement);

const render = (node) =>
  root.render(
    <React.StrictMode>
      <SentryErrorBoundary>{node}</SentryErrorBoundary>
    </React.StrictMode>,
  );

function renderLoadingState() {
  render(
    <ThemeProvider>
      <FullScreenMessage loading />
    </ThemeProvider>,
  );
}

function renderErrorState(message, error) {
  render(
    <ThemeProvider>
      <FullScreenMessage errorMessage={message} error={error} />
    </ThemeProvider>,
  );
}

function renderApp(msal) {
  render(
    <MsalProvider instance={msal}>
      <ThemeProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ThemeProvider>
    </MsalProvider>,
  );
}

renderLoadingState();

registerServiceWorker();

(async () => {
  try {
    await initializeMsal();
    const msal = await getMsalInstance();
    if (!msal) throw new Error("MSAL instance unavailable");
    renderApp(msal);
  } catch (error) {
    devLog("error", "Failed to initialize MSAL client", error);

    const missingClientId = error?.code === "MSAL_CONFIG_MISSING_CLIENT_ID";
    const message = missingClientId
      ? "Sign-in temporarily unavailable. Azure B2C configuration is missing."
      : "Sign-in temporarily unavailable. Please try again later.";

    renderErrorState(message, error);
  }
})();
